import { useState, useEffect } from 'react';
import axios from 'axios';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Package } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ['Base Cabinets', 'Wall Cabinets', 'Tall Cabinets', 'Specialty Cabinets'];
const STYLES = ['Shaker', 'Modern', 'Traditional', 'Transitional', 'Farmhouse'];
const COLORS = ['White', 'Grey', 'Natural Oak', 'Espresso', 'Navy Blue', 'Black'];
const FINISHES = ['Painted', 'Stained', 'Matte', 'High Gloss'];

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [salePrice, setSalePrice] = useState('');
  const [category, setCategory] = useState('');
  const [style, setStyle] = useState('');
  const [color, setColor] = useState('');
  const [finish, setFinish] = useState('');
  const [dimensions, setDimensions] = useState('');
  const [materials, setMaterials] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [features, setFeatures] = useState('');
  const [sku, setSku] = useState('');
  const [inStock, setInStock] = useState(true);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setName('');
    setDescription('');
    setPrice('');
    setSalePrice('');
    setCategory('');
    setStyle('');
    setColor('');
    setFinish('');
    setDimensions('');
    setMaterials('');
    setImageUrl('');
    setFeatures('');
    setSku('');
    setInStock(true);
  };

  const openCreateDialog = () => {
    setEditingProduct(null);
    resetForm();
    setIsDialogOpen(true);
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setName(product.name);
    setDescription(product.description);
    setPrice(String(product.price));
    setSalePrice(product.sale_price ? String(product.sale_price) : '');
    setCategory(product.category);
    setStyle(product.style);
    setColor(product.color);
    setFinish(product.finish);
    setDimensions(product.dimensions);
    setMaterials(product.materials);
    setImageUrl(product.images.join(', '));
    setFeatures(product.features.join(', '));
    setSku(product.sku);
    setInStock(product.in_stock);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const data = {
        name,
        description,
        price: parseFloat(price),
        sale_price: salePrice ? parseFloat(salePrice) : null,
        category,
        style,
        color,
        finish,
        dimensions,
        materials,
        images: imageUrl.split(',').map(s => s.trim()).filter(s => s),
        features: features.split(',').map(s => s.trim()).filter(s => s),
        sku,
        in_stock: inStock
      };

      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, data);
        toast.success('Product updated!');
      } else {
        await axios.post(`${API}/admin/products`, data);
        toast.success('Product created!');
      }

      setIsDialogOpen(false);
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Error saving product');
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await axios.delete(`${API}/admin/products/${productId}`);
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Error deleting product');
    }
  };

  return (
    <AdminLayout title="Products">
      <div data-testid="admin-products">
        <div className="flex justify-between items-center mb-6">
          <p className="text-[#6B7280]">{products.length} products</p>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreateDialog} data-testid="add-product-btn">
                <Plus size={18} className="mr-2" />
                Add Product
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingProduct ? 'Edit Product' : 'Add New Product'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="product-form">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="form-label">Product Name *</label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} required />
                  </div>
                  <div className="col-span-2">
                    <label className="form-label">Description *</label>
                    <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} required />
                  </div>
                  <div>
                    <label className="form-label">Price *</label>
                    <Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} step="0.01" required />
                  </div>
                  <div>
                    <label className="form-label">Sale Price</label>
                    <Input type="number" value={salePrice} onChange={(e) => setSalePrice(e.target.value)} step="0.01" />
                  </div>
                  <div>
                    <label className="form-label">Category *</label>
                    <Select value={category} onValueChange={setCategory}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="form-label">Style *</label>
                    <Select value={style} onValueChange={setStyle}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select style" />
                      </SelectTrigger>
                      <SelectContent>
                        {STYLES.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="form-label">Color *</label>
                    <Select value={color} onValueChange={setColor}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select color" />
                      </SelectTrigger>
                      <SelectContent>
                        {COLORS.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="form-label">Finish *</label>
                    <Select value={finish} onValueChange={setFinish}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select finish" />
                      </SelectTrigger>
                      <SelectContent>
                        {FINISHES.map((f) => (
                          <SelectItem key={f} value={f}>{f}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="form-label">Dimensions *</label>
                    <Input value={dimensions} onChange={(e) => setDimensions(e.target.value)} placeholder='e.g., 24" W x 34.5" H x 24" D' required />
                  </div>
                  <div>
                    <label className="form-label">Materials *</label>
                    <Input value={materials} onChange={(e) => setMaterials(e.target.value)} placeholder="e.g., Solid Birch Wood" required />
                  </div>
                  <div>
                    <label className="form-label">SKU *</label>
                    <Input value={sku} onChange={(e) => setSku(e.target.value)} required />
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" checked={inStock} onChange={(e) => setInStock(e.target.checked)} id="in_stock" />
                    <label htmlFor="in_stock" className="text-sm">In Stock</label>
                  </div>
                </div>

                <div>
                  <label className="form-label">Image URLs (comma-separated) *</label>
                  <Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://..., https://..." />
                </div>

                <div>
                  <label className="form-label">Features (comma-separated)</label>
                  <Input value={features} onChange={(e) => setFeatures(e.target.value)} placeholder="e.g., Soft-close hinges, Dovetail drawers" />
                </div>

                <div className="flex justify-end gap-4 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                  <Button type="submit">{editingProduct ? 'Update Product' : 'Create Product'}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="spinner" /></div>
        ) : products.length === 0 ? (
          <div className="text-center py-20 bg-white">
            <Package size={48} className="mx-auto text-[#E5E7EB] mb-4" />
            <h3 className="font-semibold text-[#1A1A1A] mb-2">No products yet</h3>
            <p className="text-[#6B7280] mb-4">Add your first product to get started</p>
          </div>
        ) : (
          <div className="bg-white shadow-sm overflow-x-auto">
            <table className="w-full" data-testid="products-table">
              <thead>
                <tr className="border-b border-[#E5E7EB] bg-[#F9F7F2]">
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#6B7280]">Product</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#6B7280]">Category</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#6B7280]">Price</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#6B7280]">Stock</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-[#6B7280]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.id} className="border-b border-[#E5E7EB]">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-[#F9F7F2] flex-shrink-0">
                          <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                        </div>
                        <div>
                          <p className="font-medium text-[#1A1A1A]">{product.name}</p>
                          <p className="text-xs text-[#6B7280]">{product.sku}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-[#6B7280]">{product.category}</td>
                    <td className="py-3 px-4">
                      {product.sale_price ? (
                        <div>
                          <span className="font-medium text-[#EF4444]">${product.sale_price.toFixed(2)}</span>
                          <span className="text-sm text-[#9CA3AF] line-through ml-2">${product.price.toFixed(2)}</span>
                        </div>
                      ) : (
                        <span className="font-medium">${product.price.toFixed(2)}</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${product.in_stock ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {product.in_stock ? 'In Stock' : 'Out of Stock'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="icon" onClick={() => openEditDialog(product)} data-testid={`edit-product-${product.id}`}>
                          <Edit size={16} />
                        </Button>
                        <Button variant="outline" size="icon" onClick={() => handleDelete(product.id)} className="text-red-500 hover:text-red-600" data-testid={`delete-product-${product.id}`}>
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
